/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Platform,
  Dimensions,
  KeyboardAvoidingView,
  TextInput,
  RefreshControl,
} from "react-native";
import { useTheme } from "../theme";

import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store"; // Import RootState for Redux state type
import { resetShiftHandoverState } from "../store/slices/shiftHandOverSlice";
import { getYearOptions } from "../utils/filterOptions";
import {
  setAuditCompletionStatus,
  resetAllReviewAuditData,
  clearReviewAuditData,
} from "../store/slices/qualityAuditSlice";
import LHOverlay from "../components/LHOverlay";
import LhCardDisplay from "../components/LhCardDisplay";

import { DropDownOption, FilterItem } from "../components/LHFilter";
import LhButton from "../components/LhButton";

// Import the new components
import QualityAuditBegin from "../components/qualityAudit/QualityAuditBegin";
import QualityAuditWelcomeCard from "../components/qualityAudit/QualityAuditWelcomeCard";
import AuditInsightsTab from "../components/qualityAudit/AuditInsightsTab";

import { createApiClient, apiClient, getTimezoneOffsetString } from "../api/apiClient"; // Corrected API client import
import { useToast } from "../components/LhToastContainer";
import {
  getPeriodOptions,
  getWeekOptions,
  getPreviousPeriodAndWeek,
} from "../utils/filterOptions";
import { formatUserName } from "../utils/emailTouserName";
import QualityAuditMetrics from "../components/qualityAudit/QualityAuditMetrics";
import LhActionPlanToggleSwitch from "../components/LhActionPlanToggleSwitch";
import { handleQualityAuditErrorWithFriendlyMessage, QUALITY_AUDIT_API_NAMES } from "../store/utils/qualityAuditErrorHandler";
import QualityAuditErrorModal from "./qualityAudit/components/QualityAuditErrorModal"; // created component for QA centralized errors

interface QualityAuditProps {
  id: number;
  observations?: string;
  observation_id?: number | null; // Ensure observation_id is included
  miscellaneousObservations?: string;
  ConservedWith?: boolean;
  PossibleSolutions?: string;
  region?: string; // Added region field
  [key: string]: any; // Allow for additional fields
}
interface QualityAuditScreenProps {
  onBackPress: () => void;
  onRackingInspClick?: () => void;
}
// Import the redirect screen
import QualityAuditRedirect from "./QualityAuditRedirect";
import { setQuestions } from "../store/slices/leadershipWalkRedirectSlice";
import { setmodifiedBy, setRegion } from "../store/slices/qualityAuditSlice";
import FontFamily from "../constants/fonts";
// Utility function to format email to name
const formatEmailToName = (email: string): string => {
  if (!email) {
    return "";
  }
  const namePart = email.split("@")[0];
  const names = namePart.split(".");
  return names
    .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
    .join(" ")
    .replace(/Contractor$/, ""); // Remove "Contractor" suffix if present
};

// Utility function to format date as "MON DD,YYYY,hh:mm AM/PM"
const formatCustomDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = months[date.getMonth()];
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  return `${month} ${day},${year},${hours}:${minutes} ${ampm}`;
};

const QualityAudit: React.FC<
  QualityAuditScreenProps & { selectedDatesPayload?: string }
> = ({ onBackPress, onRackingInspClick, selectedDatesPayload }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";

  // --- Helper functions (must be after state declarations) ---
  const dispatch = useDispatch(); // <-- Add this line to get the dispatch function

  const [selectedRowData, setSelectedRowData] = useState<QualityAuditProps | null>(null);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  // Redux state values
  const {
    selectedLocation,
    userEmail,
    period,
    week,
    selectedRoleFromRedux,
    reviewAuditData,
    selectedRegionFromRedux,
    selectedDatesFromRedux,
    userRoleId,
  } = useSelector((state: RootState) => ({
    selectedLocation: state.appData.selectedLocation || state.uiPreferences.selectedLocation,
    userEmail: state.auth.user?.username,
    period: state.appData.periodWeek?.data?.period,
    week: state.appData.periodWeek?.data?.week,
    selectedRoleFromRedux: state.qualityAudit.selectedRole,
    reviewAuditData: state.qualityAudit.reviewAuditData,
    selectedRegionFromRedux: state.qualityAudit.selectedRegion,
    selectedDatesFromRedux: state.qualityAudit.selectedDates || [],
    userRoleId: state.appData.userInfo?.roleId,
  }));

  // Filter state
  const defaultFilters = {
    submitterName: "",
    period: "",
    week: "",
    year: "",
    location: "",
    auditType: "",
    auditorName: "",
    auditOutcome: "",
    status: "",
  };
  const [filters, setFilters] = useState({ ...defaultFilters });
  const [appliedFilters, setAppliedFilters] = useState({ ...defaultFilters });

  // Data and loading state
  const [tableData, setTableData] = useState<QualityAuditProps[]>([]);
  const [auditInsightsData, setAuditInsightsData] = useState<QualityAuditProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Overlay visibility state
  const [showQualityAuditRedirect, setShowQualityAuditRedirect] = useState(false);
  const [showPerformanceOverlay, setShowPerformanceOverlay] = useState(false);
  const [showReviewOverlay, setShowReviewOverlay] = useState(false);
  const [showAddOverlay, setShowAddOverlay] = useState(false);
  const [isBeginFilterOverlayVisible, setBeginFilterOverlayVisible] = useState(false);

  // Performance review state
  const [performanceRowId, setPerformanceRowId] = useState<number | null>(null);
  const [performanceReviewComment, setPerformanceReviewComment] = useState("");

  // Audit filter state
  const [auditFilters, setAuditFilters] = useState<{
    role: string;
    region?: string;
    email?: string;
    regionOptions?: DropDownOption[];
  }>({
    role: "",
    region: "",
    email: "Keerthi.Velagala.Contractor@pepsico.com",
  });
  const [showValidation, setShowValidation] = useState(false);
  const [isFromBeginButton, setIsFromBeginButton] = useState(false);

  // SPARC Walk state
  const [showSparcWalkLoader, setShowSparcWalkLoader] = useState(false);
  const [sparcWalkReady, setSparcWalkReady] = useState(false);
  const [currentSparcWalkObs, setCurrentSparcWalkObs] = useState<any>(null);

  // Review overlay state
  const [reviewType, setReviewType] = useState<{ id: string; displayText: string } | null>(null);
  const [reviewStatus, setReviewStatus] = useState<{ id: string; displayText: string }[]>([]);
  const [reviewComment, setReviewComment] = useState("");
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  // Add overlay state
  const [addButtonEnabled, setAddButtonEnabled] = useState(false);
  const [submissionType, setSubmissionType] = useState<{ id: string; displayText: string } | null>(null);
  const [submissionDate, setSubmissionDate] = useState<{ id: string; displayText: string } | null>(null);
  const [submissionTypeOptions, setSubmissionTypeOptions] = useState<DropDownOption[]>([]);
  const [submissionDateOptions, setSubmissionDateOptions] = useState<DropDownOption[]>([]);

  // Audit completion tracking state
  const [completedSiteLeaderCount, setCompletedSiteLeaderCount] = useState<{[week: string]: number}>({
    "1": 0, "2": 0, "3": 0, "4": 0
  });
  const [completedRegionalLeaderCount, setCompletedRegionalLeaderCount] = useState<{[week: string]: number}>({
    "1": 0, "2": 0, "3": 0, "4": 0
  });

  // Audit insights filter state
  const [showAuditActiveOnly, setShowAuditActiveOnly] = useState(false);

  // Random observations state
  const [randomObservations, setRandomObservations] = useState<any[]>([]);
  const [currentObservationIndex, setCurrentObservationIndex] = useState(0);
  const [observationDetails, setObservationDetails] = useState<any>(null);
  const [isRandomObsLoading, setIsRandomObsLoading] = useState(false);
  const [randomObsError, setRandomObsError] = useState<string | null>(null);
  const [addOverlayError, setAddOverlayError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Hooks
  const { showToast } = useToast();
  const handleOpenReviewOverlay = () => setShowReviewOverlay(true);

  // Update fetchTableData and fetchAuditInsightsData to be accessible
  const fetchTableData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const api = createApiClient();
      const response = await apiClient(api, "/quality-audit/audits", "GET");
      const apiData = response.data;
      const formattedData = apiData.map((item: any) => ({
        submitterName: item.created_by,
        typeOfAudit: "Site", // Assuming "Site" as a placeholder
        typeOfSubmission: "Pulse Check", // Placeholder
        auditorName: item.created_by || "Unknown",
        siteRegion: item.observation_type === "Site" ? item.site : item.region, // Updated logic
        dateTime: new Date(item.created_at).toLocaleString(),
        finalizedDate: new Date(item.modified_at).toLocaleString(),
        _finalizedDateMs: item.modified_at ? new Date(item.modified_at).getTime() : 0,
        review_period: item.review_period,
        review_week: item.review_week,
        qa_period: item.qa_period,
        qa_week: item.qa_week,
        auditStatus: item.status,
        auditOutcome: "", // Placeholder for outcome
        comment: "", // Placeholder for comment
        performanceReview: "", // Placeholder for performance review
        auditLog: "View", // Placeholder for audit log
        submissionRecord: "View", // Placeholder for submission record
        assessorssignature: "",
        miscellaneousObservations: "",
        region: item.region || "", // Added region field
        // Added totalCountofObservations field
      }));

      setTableData(formattedData);
    } catch (err: any) {
      const qaErr = handleQualityAuditErrorWithFriendlyMessage(
        QUALITY_AUDIT_API_NAMES.FETCH_AUDITS,
        err,
        true,
        { endpoint: '/quality-audit/audits' }
      );
      setError(qaErr.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuditInsightsData = React.useCallback(
    async (filterParams?: any) => {
      console.log('[MOBILE DEBUG] fetchAuditInsightsData called with:', filterParams);
      setIsLoading(true);
      setError(null);
      // make `query` available to the catch block
      let query = '';
      try {
        const api = createApiClient();
        const clientTimezone = getTimezoneOffsetString();

        // Build query string from filters (ignore empty values)
        // Start with base parameters
        query = `pageNumber=1&pageSize=200&clientTimezone=${encodeURIComponent(clientTimezone)}`;
        
        // Add site parameters only if not being filtered by location
        const isFilteringByLocation = filterParams && filterParams.location && filterParams.location !== "";
        if (!isFilteringByLocation && selectedLocation?.displayText) {
          query += `&site=${encodeURIComponent(selectedLocation.displayText)}&audited_location=${encodeURIComponent(selectedLocation.displayText)}`;
        }
        
        if (filterParams) {
          console.log('[MOBILE DEBUG] filterParams received:', filterParams);
          const filterQuery = Object.entries(filterParams)
            .filter(([_, v]) => v && v !== "")
            .map(([k, v]) => {
              console.log('[MOBILE DEBUG] Processing filter:', k, '=', v);
              // Map frontend filter keys to API parameter names
              let apiKey = k;
              switch (k) {
                case "submitterName":
                  apiKey = "submitter_name";
                  break;
                case "auditType":
                  apiKey = "audit_type";
                  break;
                case "auditorName":
                  apiKey = "auditor_name";
                  break;
                case "auditOutcome":
                  apiKey = "audit_outcome";
                  break;
                case "location":
                  apiKey = "site";
                  break;
              }
              console.log('[MOBILE DEBUG] Mapped to API key:', apiKey);
              return `${encodeURIComponent(apiKey)}=${encodeURIComponent(
                String(v)
              )}`;
            })
            .join("&");

          if (filterQuery) {
            console.log('[MOBILE DEBUG] filterQuery string:', filterQuery);
            query += `&${filterQuery}`;
          }
        }

        console.log('[MOBILE DEBUG] Quality Audit API URL:', `/quality-audit?${query}`);
        const response = await apiClient(api, `/quality-audit?${query}`, "GET");
        let apiData = response.data;
        console.log('[MOBILE DEBUG] API response data length:', apiData?.length || 0);
        console.log('[MOBILE DEBUG] User roleId:', userRoleId);

        // --- Role-based filtering logic ---
        // If user role is not 1,2,3, or 15 (Super User, COE, Region Leader, or role 15),
        // filter to show only audits where both audited_location and site match selectedLocation
        const privilegedRoles = [1, 2, 3, 15];
        const isPrivilegedUser = userRoleId && privilegedRoles.includes(userRoleId);
        
        if (!isPrivilegedUser && selectedLocation?.displayText) {
          const locationText = String(selectedLocation.displayText).toLowerCase();
          apiData = apiData.filter((item: any) => {
            const auditedLocation = item.audited_location ? String(item.audited_location).toLowerCase() : '';
            const site = item.site ? String(item.site).toLowerCase() : '';
            // Both audited_location and site must match selectedLocation
            return  site === locationText;
          });
          console.log('[MOBILE DEBUG] Filtered data for non-privileged user, length:', apiData?.length || 0);
        } else {
          console.log('[MOBILE DEBUG] Privileged user or no location filter - showing all matching audits');
        }

        // --- Session grouping logic ---
        // Group by audit session: review_period + review_week + submitter + role + location.
        // This is used ONLY to assign a shared auditId to observations in the same session
        // and to build the completion status map. Every individual record still appears as
        // its own row in the dashboard.
        const grouped: { [key: string]: any[] } = {};
        apiData.forEach((item: any) => {
          const submitter = item.Submitted_By || '';
          const role = item.role || '';
          const hasRegion = item.region && item.region !== '' && item.region !== 'null';
          const location = hasRegion ? item.region : (item.site || '');
          const key = `${item.review_period}_${item.review_week}_${submitter}_${role}_${location}`;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(item);
        });

        // Assign a shared auditId to every item in each session group
        Object.values(grouped).forEach((items: any[]) => {
          const rep = items[0];
          const hasReg = rep.region && rep.region !== '' && rep.region !== 'null';
          const sessionAuditId = hasReg
            ? `P${rep.review_period}W${rep.review_week}_${rep.region}_${rep.Submitted_By}`
            : `P${rep.review_period}W${rep.review_week}_${rep.site}_${rep.Submitted_By}`;
          items.forEach((entry: any) => {
            entry.auditId = sessionAuditId;
          });
        });

        // Flatten: every observation record is its own dashboard row
        const processedData = Object.values(grouped).flat();
        console.log("Individual processedData count:", processedData.length);
        // Map API response to table data format for Audit Insights
        const formattedData = processedData.map((item: any, idx: number) => ({
          id: item.id !== undefined && item.id !== null ? item.id : idx, // Always assign a unique id
          submitterName: item.Submitted_By,
          typeOfAudit: item.role === "Site Leader" ? item.site : item.region,
          typeOfSubmission: item.observation_type,
          auditorName: item.created_by || "Unknown",
          dateTime: item.created_at ? formatCustomDate(item.created_at) : "",
          finalizedDate: item.modified_at
            ? formatCustomDate(item.modified_at)
            : "",
          _finalizedDateMs: item.modified_at ? new Date(item.modified_at).getTime() : 0,
          review_period: item.review_period,
          review_week: item.review_week,
          qa_period: item.qa_period,
          qa_week: item.qa_week,
          auditId: item.auditId,
          actualStatus: item.actual_status || "",
          performanceReview: item.performance_review,
          role: item.role,
          observation_id: item.observation_id,
          improvement_area: item.improvement_area || "",
          comments: item.comments || "",
          totalCountofObservations: item.totalCountofObservations || 0,
          rawRank: item.raw_rank || 0,
          status: item.status || "",
          auditOutcome: item.rating,
          region: item.region || "", // Added region field
        }));
        setAuditInsightsData(formattedData);
        console.log("Formatted Audit Insights Data:", formattedData); // Log formatted data for debugging

        // --- Calculate completed site leader and Region Leader audits for each week in current period ---
        const currentPeriod =
          typeof period === "string" || typeof period === "number"
            ? String(period)
            : "";
        const currentWeek =
          typeof week === "string" || typeof week === "number"
            ? String(week)
            : "";
        
        console.log("Current Period:", currentPeriod, "Current Week:", currentWeek);
        
        // Initialize counts for each week
        const siteLeaderCounts: {[week: string]: number} = { "1": 0, "2": 0, "3": 0, "4": 0 };
        const regionLeaderCounts: {[week: string]: number} = { "1": 0, "2": 0, "3": 0, "4": 0 };
        
        // Calculate completed audits mapping audit period/week to progress bar weeks
        // Note: The period/week in audit data represents the week being audited (one week behind submission)
        // Progress Week 1 = Audits of Previous Period's Last Week (done during current P W1)
        // Progress Week 2 = Audits of Current Period W1 (done during current P W2)
        // Progress Week 3 = Audits of Current Period W2 (done during current P W3)
        // Progress Week 4 = Audits of Current Period W3 (done during current P W4)
        
        const currentPeriodNum = parseInt(currentPeriod);
        const currentWeekNum = parseInt(currentWeek);
        
        processedData.forEach((item: any) => {
          const itemWeek = parseInt(String(item.qa_week));
          const itemPeriod = parseInt(String(item.qa_period));
          const itemStatus = String(item.status).toLowerCase();
          const itemRole = String(item.role).toLowerCase();
          
          if (itemStatus === "completed") {
            // Determine which progress bar week this audit belongs to
            let progressWeek: string | null = null;
            
            // Map audit data to progress bar weeks based on when the audit was submitted (qa_period/qa_week)
            // qa_period/qa_week is the actual week the audit was done, so it maps directly to the progress bar week
            if (itemPeriod === currentPeriodNum && itemWeek === 1) {
              progressWeek = "1";
            } else if (itemPeriod === currentPeriodNum && itemWeek === 2) {
              progressWeek = "2";
            } else if (itemPeriod === currentPeriodNum && itemWeek === 3) {
              progressWeek = "3";
            } else if (itemPeriod === currentPeriodNum && itemWeek === 4) {
              progressWeek = "4";
            }
            
            if (progressWeek && parseInt(progressWeek) <= currentWeekNum) {
              // Count Site Leader audits
              if (itemRole === "site leader") {
                siteLeaderCounts[progressWeek]++;
              }
              
              // Count Region Leader audits (with location match)
              if (itemRole === "region leader" || itemRole === "coe") {
                // For non-privileged users, check site match (same as display filtering logic)
                // For privileged users, check audited_location match (original behavior)
                const locationMatch = isPrivilegedUser 
                  ? (item.audited_location && 
                     String(item.audited_location).toLowerCase() === String(selectedLocation?.displayText).toLowerCase())
                  : (item.site && 
                     String(item.site).toLowerCase() === String(selectedLocation?.displayText).toLowerCase());
                if (locationMatch) {
                  regionLeaderCounts[progressWeek]++;
                }
              }
            }
          }
        });
        
        console.log("Completed Site Leader Audits by Progress Week:", siteLeaderCounts);
        console.log("Completed Regional Leader Audits by Progress Week:", regionLeaderCounts);
        
        setCompletedSiteLeaderCount(siteLeaderCounts);
        setCompletedRegionalLeaderCount(regionLeaderCounts);

        // --- Calculate audit completion status for each auditId ---
        const auditCompletionStatusMap: {
          [auditId: string]: {
            currentCount: number;
            totalRequired: number;
            isCompleted: boolean;
          };
        } = {};

        // Build completion status map per session (auditId)
        // Use the highest raw_rank seen in the session as currentCount
        Object.values(grouped).forEach((items: any[]) => {
          if (items.length === 0) return;
          const auditId = items[0].auditId;
          if (!auditId) return;
          const totalRequired = items[0].totalCountofObservations || 0;
          const maxRank = Math.max(...items.map((i) => Number(i.raw_rank) || 0));
          const isCompleted = items.every((i) => i.status === 'Completed');

          auditCompletionStatusMap[auditId] = {
            currentCount: maxRank,
            totalRequired,
            isCompleted,
          };
        });

        // Store completion status in Redux
        dispatch(setAuditCompletionStatus(auditCompletionStatusMap));
        console.log(
          "Audit completion status calculated:",
          auditCompletionStatusMap
        );
      } catch (err: any) {
        const qaErr = handleQualityAuditErrorWithFriendlyMessage(
          QUALITY_AUDIT_API_NAMES.FETCH_AUDITS_QUERY,
          err,
          true,
          { query }
        );
        setError(qaErr.message);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedLocation, week]
  );

  // Function to handle filter changes with auto-apply (like SparcWalkMetrics)
  const handleFilterChange = (
    key: string,
    value: DropDownOption | DropDownOption[] | null
  ) => {
    console.log('[MOBILE DEBUG] handleFilterChange called:', { key, value });
    console.log('[MOBILE DEBUG] value structure:', JSON.stringify(value));
    
    // Handle the value - it might be a wrapper {id, label, value} or the original option
    const extractId = (val: any): string => {
      if (!val) return "";
      
      console.log('[MOBILE DEBUG] extractId input:', val);
      
      // First check if there's a nested 'value' property (wrapper from FilterDropdown)
      // FilterDropdown wraps options as {id, label, value: originalOption}
      if (val.value && typeof val.value === 'object' && val.value.id !== undefined) {
        console.log('[MOBILE DEBUG] Using nested value.id:', val.value.id);
        return String(val.value.id);
      }
      
      // If it has an id property directly, use that
      if (val.id !== undefined) {
        console.log('[MOBILE DEBUG] Using direct id:', val.id);
        return String(val.id);
      }
      
      // Last resort fallback
      console.log('[MOBILE DEBUG] Fallback to string conversion:', val);
      return String(val);
    };
    
    // Update filter state
    const updatedFilters = {
      ...filters,
      [key]: Array.isArray(value)
        ? value.map((v) => extractId(v)).join(",")
        : extractId(value),
    };
    
    console.log('[MOBILE DEBUG] updatedFilters:', updatedFilters);
    
    setFilters(updatedFilters);
    
    // Auto-apply filters immediately when selection changes
    setAppliedFilters(updatedFilters);
    fetchAuditInsightsData(updatedFilters);
  };

  // Reset filters: clear filters and call API with no filters
  const resetFilters = React.useCallback(async () => {
    setFilters({ ...defaultFilters });
    setAppliedFilters({ ...defaultFilters });
    await fetchAuditInsightsData();
  }, [fetchAuditInsightsData]);

  const onRefresh = React.useCallback(async () => {
    setRefreshing(true);
    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('refresh_timeout')), 3000)
    );
    try {
      await Promise.race([fetchAuditInsightsData(), timeout]);
    } catch {
      // timeout or error — silently cancel
    } finally {
      setRefreshing(false);
    }
  }, [fetchAuditInsightsData]);

  const isMobile = Dimensions.get("window").width < 768;

  // Helper function to get submitter name options
  const getSubmitterNameOptions = (
    data: any[]
  ): { id: string; displayText: string }[] => {
    const names = Array.from(
      new Set(
        (data || []).map((item: any) => item.submitterName).filter(Boolean)
      )
    );
    return names.map((name: string) => ({
      id: name,
      displayText: formatUserName(name),
    }));
  };

  // Helper function to get auditor name options
  const getAuditorNameOptions = (
    data: any[]
  ): { id: string; displayText: string }[] => {
    const names = Array.from(
      new Set((data || []).map((item: any) => item.auditorName).filter(Boolean))
    );
    return names.map((name: string) => ({
      id: name,
      displayText: formatUserName(name),
    }));
  };

  // Helper function to get location options
  const getLocationOptions = (
    data: any[]
  ): { id: string; displayText: string }[] => {
    const locations = Array.from(
      new Set((data || []).map((item: any) => item.typeOfAudit).filter(Boolean))
    );
    return locations.map((location: string) => ({
      id: location,
      displayText: location,
    }));
  };

  const filterOptions1: FilterItem[] = [
    {
      key: "submitterName",
      label: "Submitter Name",
      selectedOptionId: filters.submitterName || "",
      options: getSubmitterNameOptions(auditInsightsData),
    },
    {
      key: "period",
      label: "Review Period",
      selectedOptionId: filters.period || "",
      options: getPeriodOptions()
        .filter((opt) => opt.id && opt.id !== "")
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map((opt) => ({
          ...opt,
          displayText: `P${opt.id}`,
        })),
    },
    {
      key: "week",
      label: "Review Week",
      selectedOptionId: filters.week || "",
      options: getWeekOptions()
        .filter((opt) => opt.id && opt.id !== "")
        .map((opt) => ({
          ...opt,
          displayText: `W${opt.id}`,
        })),
    },
    {
      key: "year",
      label: "Year",
      selectedOptionId: filters.year || "",
      options: getYearOptions(),
    },
    {
      key: "location",
      label: "Location",
      selectedOptionId: filters.location || "",
      options: getLocationOptions(auditInsightsData),
    },
    {
      key: "auditType",
      label: "Audit Type",
      selectedOptionId: filters.auditType || "",
      options: [
        { id: "Site Leader", displayText: "Site" },
        { id: "Region Leader", displayText: "Region" },
        { id: "COE", displayText: "COE" },
      ],
    },
    {
      key: "auditorName",
      label: "Auditor Name",
      selectedOptionId: filters.auditorName || "",
      options: getAuditorNameOptions(auditInsightsData),
    },
    {
      key: "auditOutcome",
      label: "Audit Outcome",
      selectedOptionId: filters.auditOutcome || "",
      options: [
        { id: "0", displayText: "0" },
        { id: "1", displayText: "1" },
        { id: "2", displayText: "2" },
        { id: "3", displayText: "3" },
      ],
    },
    {
      key: "status",
      label: "Status",
      selectedOptionId: filters.status || "",
      options: [
        { id: "In Progress", displayText: "In Progress" },
        { id: "Completed", displayText: "Completed" },
      ],
    },
  ];

  const tableColumns1 = [
    {
      columnKey: "submitterName",
      header: "Submitter Name",
      formatter: (value: string) => formatEmailToName(value),
    },
    //  { columnKey: "auditId", header: "Audit Id" },
    {
      columnKey: "Tracker",
      header: "Progress",
      getSortValue: (item: any) => Number(item.rawRank) || 0,
      renderCell: (item: any) => (
        <Text style={styles.tableCellText}>{`${item.rawRank ?? 0}/${
          item.totalCountofObservations ?? 0
        }`}</Text>
      ),
    },
    { columnKey: "auditOutcome", header: "Audit Outcome" },
    { columnKey: "typeOfAudit", header: "Type of Audit" },
    { columnKey: "typeOfSubmission", header: "Type of Submission" },
    {
      columnKey: "auditorName",
      header: "Auditor Name",
      formatter: (value: string) => formatEmailToName(value),
    },
    // {
    //   columnKey: "dateTime",
    //   header: "Date & Time of Audit",
    // },
    // { columnKey: "siteRegion", header: "Site/Region" },
    {
      columnKey: "finalizedDate",
      header: "Finalized Time",
      getSortValue: (item: any) => item._finalizedDateMs || 0,
    },
    {
      columnKey: "reviewWeek",
      header: "Review Week",
      getSortValue: (item: any) => {
        const p = Number(item.review_period) || 0;
        const w = Number(item.review_week) || 0;
        return p * 100 + w;
      },
      renderCell: (item: any) => {
        const hasReviewData = item.review_period != null && item.review_week != null;
        return (
          <Text style={styles.tableCellText}>
            {hasReviewData ? `P${item.review_period}W${item.review_week}` : 'N/A'}
          </Text>
        );
      },
    },
    {
      columnKey: "qaWeek",
      header: "QA Week",
      getSortValue: (item: any) => {
        const p = Number(item.qa_period) || 0;
        const w = Number(item.qa_week) || 0;
        return p * 100 + w;
      },
      renderCell: (item: any) => {
        const hasQAData = item.qa_period != null && item.qa_week != null;
        return (
          <Text style={styles.tableCellText}>
            {hasQAData ? `P${item.qa_period}W${item.qa_week}` : 'N/A'}
          </Text>
        );
      },
    },
    // {
    //   columnKey: "auditOutcome",
    //   header: "Audit Outcome",
    //   render: (value: string) => {
    //     const outcome = filterOptions1?.find(f => f.key === "auditOutcome")?.options?.find(o => o.id === value);
    //     return outcome ? outcome.displayText : value;
    //   }
    // },
    // { columnKey: "comment", header: "Comment" },
    // { columnKey: "improvementArea", header: "Improvement Area", },

    // {columnKey: "performanceReview", header: "Performance Review"},
    {
      columnKey: "performanceReview",
      header: "Performance Review",
      renderCell: (item: any) => {
        // Parse auditOutcome as a number
        const auditOutcomeNum = Number(item.auditOutcome);
        // Determine if performance review is done
        const isReviewDone = !!item.performanceReview;
        let toggleState: "no-action-plan" | "pending-completion" | "completed" =
          "no-action-plan";
        let disabled = false;

        if (auditOutcomeNum > 1) {
          toggleState = "no-action-plan"; // Gray, disabled
          disabled = true;
        } else if (auditOutcomeNum === 0 || auditOutcomeNum === 1) {
          if (isReviewDone) {
            toggleState = "completed"; // Grey
          } else {
            toggleState = "pending-completion"; // Grey
          }
          disabled = false;
        }

        return (
          <LhActionPlanToggleSwitch
            state={toggleState}
            disabled={disabled}
            onValueChange={() => {
              if (!disabled && toggleState === "pending-completion") {
                setPerformanceRowId(item.id);
                setShowPerformanceOverlay(true);
              }
            }}
          />
        );
      },
    },
    {
      columnKey: "status",
      header: "Status",
    },
  ];

  const handleRowClick = async (rowData: QualityAuditProps) => {
    console.log("Row data clicked:", rowData.role);
    // Store the role in selectedRole state in Redux
    if (rowData.role) {
      dispatch({ type: "qualityAudit/setSelectedRole", payload: rowData.role });
    }
    if ((rowData.role === "Region Leader" || rowData.role === "COE") && rowData.typeOfAudit) {
      dispatch({
        type: "qualityAudit/setSelectedRegion",
        payload: rowData.region || "",
      });
    }
    // If submitterName cell is clicked, open the correct observation in the redirect flow
    if (rowData.columnKey === "submitterName") {
      // Set flag to indicate this redirect is from row data, not Begin button
      setIsFromBeginButton(false);
      // Dispatch the selected row data to Redux
      dispatch({ type: "qualityAudit/setSelectedRowData", payload: rowData });
      // Make sure we set the role in Redux
      if (rowData.role) {
        dispatch({
          type: "qualityAudit/setSelectedRole",
          payload: rowData.role,
        });
      }
      console.log("Row data clicked:", rowData);
      let params = '';
      try {
        //  setSelectedRole(rowData.role || '');
        setIsRandomObsLoading(true);
        setRandomObsError(null);
        // Use review_period/review_week from the row, fallback to Redux/app state
        const currentPeriod = rowData.review_period
          ? String(rowData.review_period).replace(/^P/, "")
          : rowData.period
          ? String(rowData.period).replace(/^P/, "")
          : period
          ? String(period)
          : "";
        const currentWeek = rowData.review_week
          ? String(rowData.review_week).replace(/^W/, "")
          : rowData.week
          ? String(rowData.week).replace(/^W/, "")
          : week
          ? String(week)
          : "";
        
        // Calculate previous period and week
        // const { period: prevPeriod, week: prevWeek } = getPreviousPeriodAndWeek(currentPeriod, currentWeek);
        
        // assign params (declared in outer scope) so the catch block can reference it
        params = new URLSearchParams({
          period: currentPeriod,
          week: currentWeek,
          location_name: rowData.site || selectedLocation?.displayText || "",
          role: rowData.role,
          
          email: rowData.auditorName || userEmail || "",
          ...((rowData.role === "Region Leader" || rowData.role === 'COE') && rowData.region
            ? { region: rowData.region }
            : {}),
        }).toString();
        const api = createApiClient();
        const response = await apiClient(
          api,
          `/quality-audit/random-observations?${params}`,
          "GET"
        );
        if (response.data && Array.isArray(response.data)) {
          setRandomObservations(response.data);
          // Also dispatch the random observations and index to Redux
          dispatch({
            type: "qualityAudit/setRandomObservations",
            payload: response.data,
          });
          // Find the index of the clicked observation in the returned array
          let obsId = rowData.observation_id || rowData.id;
          let foundIndex = response.data.findIndex(
            (obs: any) => obs.observation_id === obsId || obs.id === obsId
          );
          if (foundIndex === -1) foundIndex = 0; // fallback to first if not found
          setCurrentObservationIndex(foundIndex);
          dispatch({
            type: "qualityAudit/setCurrentObservationIndex",
            payload: foundIndex,
          });

          setShowQualityAuditRedirect(true);
        } else {
          setRandomObsError("No data found in API response");
        }
      } catch (err: any) {
        const qaErr = handleQualityAuditErrorWithFriendlyMessage(
          QUALITY_AUDIT_API_NAMES.RANDOM_OBSERVATIONS,
          err,
          true,
          { params }
        );
        setRandomObsError(qaErr.message);
      } finally {
        setIsRandomObsLoading(false);
      }
      return;
    }
    // If performanceReview button is clicked, open performance overlay
    if (rowData.columnKey === "performanceReview") {
      setPerformanceRowId(rowData.id);
      setShowPerformanceOverlay(true);
      return;
    }
    // Default: show overlay
    setSelectedRowData(rowData);
    setOverlayVisible(true);
  };


  const closeOverlay = () => {
    setOverlayVisible(false);
    setSelectedRowData(null);
  };

  const filteredAuditInsightsData = showAuditActiveOnly
    ? auditInsightsData.filter((item) => {
        const rating = Number(item.auditOutcome);
        return (
          rating < 2 && String(item.status).toLowerCase() === "completed"
          // String(item.actualStatus).toLowerCase() === "active"
        );
      })
    : auditInsightsData;

  // Handle navigation to QualityAuditRedirect
  const handleBeginButtonClick = () => {
    // Open filter overlay instead of immediately navigating
    openBeginFilterOverlay();
  };

  // Function to open the filter overlay
  const openBeginFilterOverlay = () => {
    setBeginFilterOverlayVisible(true);
  };

  // Function to close the filter overlay with optional reset
  const closeBeginFilterOverlay = (resetFilters: boolean = true) => {
    console.log("Closing filter overlay, resetFilters:", resetFilters);
    setBeginFilterOverlayVisible(false);
    
    // Reset audit filters only if specified (e.g., when canceling)
    if (resetFilters) {
      setAuditFilters((prevFilters) => ({
        ...prevFilters,
        role: "",
        region: "",
      }));
      
      // Reset validation state
      setShowValidation(false);
    }
  };

  // Handle back button press from redirect screen
  const handleRedirectBackPress = () => {
    // Reset the values in Redux to initial ones when exiting redirect
    dispatch({ type: "qualityAudit/resetRandomObservationsAndIndex" });
    dispatch({ type: "qualityAudit/resetSelectedRowData" });
    dispatch({ type: "qualityAudit/setSelectedRole", payload: "" }); // Reset selected role
    dispatch(resetAllReviewAuditData()); // Clear all stored review data
    setShowQualityAuditRedirect(false);
  };

  // --- Modified handleSaveAuditFilters ---
  const handleSaveAuditFilters = async () => {
    console.log('[handleSaveAuditFilters] Starting with auditFilters:', JSON.stringify(auditFilters));
    console.log('[handleSaveAuditFilters] role:', auditFilters.role);
    console.log('[handleSaveAuditFilters] region:', auditFilters.region);
    console.log('[handleSaveAuditFilters] Period:', period, 'Week:', week);
    console.log('[handleSaveAuditFilters] Selected Location:', selectedLocation);
    console.log('[handleSaveAuditFilters] User Email:', userEmail);
    
    if (!auditFilters.role || auditFilters.role === "" || auditFilters.role === null) {
      console.log('[handleSaveAuditFilters] Validation failed: role is empty/null');
      setShowValidation(true);
      return;
    }
    if (
      (auditFilters.role === "Region Leader" || auditFilters.role === "COE") &&
      (!auditFilters.region || auditFilters.region === "" || auditFilters.region === null)
    ) {
      console.log('[handleSaveAuditFilters] Validation failed: Region Leader/COE selected but region is empty/null');
      setShowValidation(true);
      return;
    }
    console.log('[handleSaveAuditFilters] Validation passed');
    setShowValidation(false);
    // Set flag to indicate this redirect is from Begin button
    setIsFromBeginButton(true);
    closeBeginFilterOverlay(false); // Don't reset filters when proceeding
    setIsRandomObsLoading(true);
    setRandomObsError(null);

    // Store role in Redux
    dispatch({
      type: "qualityAudit/setSelectedRole",
      payload: auditFilters.role,
    });

    let params = '';
    try {
      // Calculate previous period and week
      const currentPeriod = period ? String(period) : "";
      const currentWeek = week ? String(week) : "";
      const { period: prevPeriod, week: prevWeek } = getPreviousPeriodAndWeek(currentPeriod, currentWeek);
      
      console.log('[handleSaveAuditFilters] Previous period:', prevPeriod, 'Previous week:', prevWeek);
      
      // declare params outside so catch can access
      params = new URLSearchParams({
        period: prevPeriod,
        week: prevWeek,
         // period:'7',
         // week: '2',
         location_name: selectedLocation?.displayText || "",
         // location:"2858",
         role: auditFilters.role,
         email: userEmail || "",
         ...((auditFilters.role === "Region Leader" || auditFilters.role === "COE") && auditFilters.region
           ? { region: auditFilters.region }
           : {}),
       }).toString();
      
      console.log('[handleSaveAuditFilters] API URL params string:', params);
      console.log('[handleSaveAuditFilters] Full API URL:', `quality-audit/random-observations?${params}`);
      
      const api = createApiClient();
      const response = await apiClient(
        api,
        `quality-audit/random-observations?${params}`,
        "GET"
      );
      
      console.log('[handleSaveAuditFilters] API response:', response);
      
      if (
        response.data &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        setRandomObservations(response.data);
        setCurrentObservationIndex(0);
        setShowQualityAuditRedirect(false); // Don't show old redirect
      } else {
        // setRandomObsError('No data found in API response');
        showToast({
          header: "Info",
          subtext: "No observation found under this location",
          variant: "info",
          position: "top",
          duration: 7000,
        });
      }
    } catch (err: any) {
      console.error('[handleSaveAuditFilters] Error:', err);
      console.error('[handleSaveAuditFilters] Error message:', err.message);
      console.error('[handleSaveAuditFilters] Error response:', err.response);
      const qaErr = handleQualityAuditErrorWithFriendlyMessage(
        QUALITY_AUDIT_API_NAMES.RANDOM_OBSERVATIONS,
        err,
        true,
        { params }
      );
      setRandomObsError(qaErr.message);
    } finally {
      setIsRandomObsLoading(false);
    }
  };

  const handleCloseReviewOverlay = () => {
    // Always clear form data when closing the overlay (unless submitting)
    // to prevent accidental submission of stale data
    if (!isSubmittingReview) {
      setShowReviewOverlay(false);
      setReviewType(null);
      setReviewStatus([]);
      setReviewComment("");
      console.log("Review overlay closed - form data cleared");
    }
    // Preserve the state of addButtonEnabled
  };

  // Accept status argument and use it in the API payload
  const handleSubmitReview = async (
    status: string,
    formData?: { reviewType: any; reviewStatus: any[]; reviewComment: string }
  ) => {
    if (!randomObservations.length) return;

    // Prevent multiple simultaneous calls
    if (isSubmittingReview) {
      console.log("Already submitting review, ignoring duplicate call");
      return;
    }

    const obs = randomObservations[currentObservationIndex];
    const currentObservationId = obs.observation_id?.toString();
    const storedReviewData = currentObservationId
      ? reviewAuditData[currentObservationId]
      : null;

    console.log("=== handleSubmitReview CALL START ===");
    console.log("Status parameter:", status);
    console.log("FormData parameter:", formData);
    console.log("Current form state:", {
      reviewType,
      reviewStatus,
      reviewComment,
    });
    console.log("Stored review data:", storedReviewData);

    setIsSubmittingReview(true);

    try {
      console.log("handleSubmitReview - Processing submission");

      // Determine values based on the status and available data
      let rating = "";
      let improvement_area = "";
      let comments = "";

      if (status.toLowerCase() === "completed") {
        // For "Complete" action: use the passed form data (actual review audit values)
        rating = formData?.reviewType?.displayText || "";
        improvement_area =
          formData?.reviewStatus && formData.reviewStatus.length > 0
            ? formData.reviewStatus
                .map((status) => status.displayText)
                .join(", ")
            : "";
        comments = formData?.reviewComment || "";
        console.log("Using form data for completed review:", {
          rating,
          improvement_area,
          comments,
        });
      } else {
        // For "Save for Later" (In Progress): check if a review audit has been submitted for this observation
        console.log(
          "Processing Save for Later - checking for stored review data..."
        );
        console.log("Stored review data available:", !!storedReviewData);
        console.log("Stored review data content:", storedReviewData);

        // Check if there's actual review data stored (not just form state)
        // Only use stored data if it contains meaningful review audit information
        const hasActualReviewData =
          storedReviewData &&
          (storedReviewData.reviewType?.displayText ||
            storedReviewData.reviewComment ||
            (storedReviewData.reviewStatus &&
              storedReviewData.reviewStatus.length > 0 &&
              storedReviewData.reviewStatus.some(
                (status) => status.displayText
              )));

        // Check if the current form has any meaningful data (user is in the middle of filling out a review)
        // BUT only use it if it's for the current observation and was intentionally filled by the user
        const hasCurrentFormData =
          formData &&
          (formData.reviewType?.displayText ||
            formData.reviewComment ||
            (formData.reviewStatus &&
              formData.reviewStatus.length > 0 &&
              formData.reviewStatus.some((status) => status.displayText)));

        console.log("Has current form data:", !!hasCurrentFormData);
        console.log("Current form data:", formData);
        console.log(
          "Has stored review data for this observation:",
          !!hasActualReviewData
        );

        // For "Save for Later", prioritize stored review data over form data
        // This ensures we don't use stale form data from previous observations
        if (hasActualReviewData) {
          // Use stored review data from a previously completed review audit
          rating = storedReviewData.reviewType?.displayText || "";
          improvement_area =
            storedReviewData.reviewStatus &&
            storedReviewData.reviewStatus.length > 0
              ? storedReviewData.reviewStatus
                  .map((status) => status.displayText)
                  .join(", ")
              : "";
          comments = storedReviewData.reviewComment || "";
          console.log("Using stored review data for save for later:", {
            rating,
            improvement_area,
            comments,
          });

          // Clear the stored review data from Redux after using it for "Save for Later"
          // This ensures it doesn't carry over to subsequent observations
          if (currentObservationId) {
            dispatch(clearReviewAuditData(currentObservationId));
            console.log(
              "Cleared stored review data from Redux after using for Save for Later"
            );
          }
        } else if (hasCurrentFormData) {
          // Only use current form data if no stored review data exists and user has intentionally filled the form
          rating = formData.reviewType?.displayText || "";
          improvement_area =
            formData.reviewStatus && formData.reviewStatus.length > 0
              ? formData.reviewStatus
                  .map((status) => status.displayText)
                  .join(", ")
              : "";
          comments = formData.reviewComment || "";
          console.log("Using current form data for save for later:", {
            rating,
            improvement_area,
            comments,
          });
        } else {
          // No meaningful review audit data exists, use default values
          rating = "Not Rated";
          improvement_area = "None";
          comments = "";
          console.log(
            "Using default values for save for later (no review audit submitted)"
          );
        }
      }

      console.log(
        "Final values - rating:",
        rating,
        "improvement_area:",
        improvement_area,
        "comments:",
        comments
      );
      console.log("audit filters role", auditFilters.role);
      console.log("row data role", selectedRowData?.role);

      const payload = {
        observation_type: obs.observation_type,
        observation_id: obs.observation_id,
        rating,
        improvement_area,
        comments,
        status, // Use the status argument here
        period: period ? String(period) : "",
        week: week ? String(week) : "",
        role:
          selectedRoleFromRedux ||
          auditFilters.role ||
          selectedRowData?.role ||
          "",
        site: obs.site || obs.siteRegion || "",
        region:
          selectedRegionFromRedux || auditFilters.region || obs.region || "",
        auditor_name: userEmail || "",
        ...((auditFilters.role === "Region Leader" || auditFilters.role === "COE" ||
          selectedRoleFromRedux === "Region Leader" || selectedRoleFromRedux === "COE") &&
        auditFilters.region
          ? { region: auditFilters.region }
          : {}),
          audited_location:selectedLocation?.displayText,
      };

      console.log("API payload:", payload);
      const api = createApiClient();
      await apiClient(api, "/quality-audit/submit", "POST", payload);
      setAddButtonEnabled(true); // Enable Next button after successful review completion

      // Close overlay and clear form data after successful submission
      if (status.toLowerCase() === "completed") {
        setShowReviewOverlay(false);
        setReviewType(null);
        setReviewStatus([]);
        setReviewComment("");
        console.log(
          "Review audit completed successfully - overlay closed and form cleared"
        );
      }
    } catch (err) {
      setAddButtonEnabled(false); // Disable Next button if review fails
      setShowReviewOverlay(false);
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleOpenAddOverlay = () => {
    const currentObservation = randomObservations[currentObservationIndex];
    if (currentObservation) {
      const walkTypeOption = {
        id:
          currentObservation.walkType ||
          currentObservation.observation_type ||
          "unknown",
        displayText:
          currentObservation.walkType ||
          currentObservation.observation_type ||
          "Unknown Walk Type",
      };

      setSubmissionTypeOptions([walkTypeOption]);
    }
    setShowAddOverlay(true);
  };

  const handleCloseAddOverlay = () => {
    setShowAddOverlay(false);
    setSubmissionType(null);
    setSubmissionDate(null);
    setAddOverlayError(null); // Clear error when closing
  };

  // Accepts an optional onError callback for error toast
  const handleAddRandomObservation = async (
    onError?: (msg: string) => void
  ) => {
    setShowAddOverlay(true); // Always keep overlay open for this action
    setAddOverlayError(null); // Reset error before API call
    const selectedDates = selectedDatesFromRedux.join(", ");
    let previous_period = getPreviousPeriodAndWeek(
      period ? String(period) : "",
      week ? String(week) : ""
    ).period;
    let previous_week = getPreviousPeriodAndWeek(
      period ? String(period) : "",
      week ? String(week) : ""
    ).week;
    const params = new URLSearchParams({period:previous_period, week:previous_week,
      location_name: selectedLocation?.displayText || "",
      role:
        auditFilters.role ||
        selectedRowData?.role ||
        selectedRoleFromRedux ||
        "",
      region: auditFilters.region || selectedRegionFromRedux || "",
      email: userEmail || "",
      observed_by: auditFilters.email || userEmail || "",
      ObservationType: submissionTypeOptions
        .map((opt) => opt.displayText)
        .join(", "),
      selectedDates: selectedDates,
    }).toString();
    try {
      setIsRandomObsLoading(true);
      setRandomObsError(null);
      // Do NOT close Review Overlay when adding - keep it open
      // setShowReviewOverlay(false);
      setReviewType(null);
      setReviewStatus([]);
      setReviewComment("");
      setAddButtonEnabled(false);
      const api = createApiClient();
      const response = await apiClient(
        api,
        `/quality-audit/getrandomobservation?${params}`,
        "GET"
      );

      // Check if the response exists and that data is not null
      if (response) {
        if (response.data == null) {
          // Data is null even though a "success" message was returned
          const msg = "No observations found for the selected criteria.";
          setAddOverlayError(msg); // Show error in overlay
          if (onError) onError(msg);
          return; // Do NOT close the overlay
        }
        // At this point, response.data is not null
        const obs = response.data;
        let details = null;
        if (obs.observation_type === "Shift Handover") {
          details = await apiClient(
            api,
            `/shift-handover-walks/${obs.observation_id}`,
            "GET"
          );
        } else if (obs.observation_type === "Pulse Check" || obs.observation_type === "Routine" || obs.observation_type === "Full") {
          details = await apiClient(
            api,
            `/observations/${obs.observation_id}`,
            "GET"
          );
        } else if (obs.observation_type === "Leadership Walk") {
          details = await apiClient(
            api,
            `/leadership-walks/${obs.observation_id}`,
            "GET"
          );
        } else if (obs.observation_type === "SPARC Walk") {
          details = await apiClient(
            api,
            `/sparc-walks/${obs.observation_id}`,
            "GET"
          );
        } else {
          // Invalid observation type
          const msg = "No observation found under selected dates";
          setAddOverlayError(msg); // Show error in overlay
          if (onError) onError(msg);
          return; // Do NOT close the overlay
        }
        // If we reach here, we have a valid observation and details
        setRandomObservations((prev) => {
          const insertIndex = currentObservationIndex + 1;
          const updated = [
            ...prev.slice(0, insertIndex),
            { ...obs, details: details?.data },
            ...prev.slice(insertIndex),
          ];
          setCurrentObservationIndex(insertIndex);
          return updated;
        });
        setShowAddOverlay(false); // Only close overlay after successful add!
        setSubmissionTypeOptions([]);
        setSubmissionDateOptions([]);
      } else {
        // No response: keep overlay open and show error inside overlay
        const msg = "No observation found under selected dates";
        setAddOverlayError(msg);
        if (onError) onError(msg);
        return;
      }
    } catch (err) {
      const errorMsg =
        err && typeof err === "object" && "message" in err
          ? (err as any).message
          : "Failed to fetch random observation";

      // Dispatch centralized error and show modal overlay
      try {
        handleQualityAuditErrorWithFriendlyMessage(
          QUALITY_AUDIT_API_NAMES.RANDOM_OBSERVATIONS,
          err,
          true,
          { params }
        );
      } catch (handlerErr) {
        console.error("Error while dispatching QA random observation error:", handlerErr);
      }

      setAddOverlayError(errorMsg); // Show error in overlay
      if (onError) onError(errorMsg);
      return;
    } finally {
      setIsRandomObsLoading(false);
    }
  };

  // Add this handler to refresh data after redirect
  const handleRedirectComplete = async () => {
    setShowQualityAuditRedirect(false);
    // Re-fetch both tables
    await fetchTableData();
    await fetchAuditInsightsData();
  };

  // Fetch regions based on role and location
  useEffect(() => {
    const fetchRegions = async () => {
      console.log("fetchRegions function called"); // Log when the function is called
      console.log("Current role in auditFilters:", auditFilters.role); // Log the current role

      if (auditFilters.role === "Region Leader" || auditFilters.role === "COE") {
        if (!userEmail) {
          console.warn("User email is not available. Cannot fetch regions.");
          return;
        }

        // declare requestUrl in the enclosing function scope so the catch block can access it
        let requestUrl = '';
        try {
          const api = createApiClient();
          console.log("Making API call to fetch regions"); // Log before API call
          const encodedEmail = encodeURIComponent(userEmail);
          const encodedLocation = selectedLocation?.displayText 
            ? encodeURIComponent(selectedLocation.displayText) 
            : "";
          requestUrl = `/quality-audit/regions?email=${encodedEmail}&location=${encodedLocation}`;

          console.log("Constructed request URL:", requestUrl); // Log the full request URL

          const response = await apiClient(api, requestUrl, "GET");

          console.log("API response:", response); // Log the API response for debugging

          const regions: { region: string | null }[] =
            response.data?.regions || [];

          setAuditFilters((prevFilters) => ({
            ...prevFilters,
            regionOptions: regions.map((regionObj, index) => ({
              id: index.toString(), // Use index as unique id
              displayText: regionObj.region || "null", // Display 'null' for null values
            })),
          }));
        } catch (err) {
          console.error("Failed to fetch regions:", err);
          // Use centralized error handler to add error and show modal overlay
          try {
            handleQualityAuditErrorWithFriendlyMessage(
              QUALITY_AUDIT_API_NAMES.FETCH_REGIONS,
              err,
              true,
              { requestUrl }
            );
          } catch (handlerErr) {
            // Fail silently if the error handler itself fails
            console.error('Error while handling QA regions error:', handlerErr);
          }
        }
      } else {
        // Reset region options if role is not Region Leader or COE
        setAuditFilters((prevFilters) => ({
          ...prevFilters,
          regionOptions: [],
        }));
      }
    };

    fetchRegions();
  }, [auditFilters.role, userEmail, selectedLocation]);

  // Fetch details for the current observation
  useEffect(() => {
    if (
      randomObservations.length > 0 &&
      currentObservationIndex < randomObservations.length
    ) {
      const obs = randomObservations[currentObservationIndex];
      const fetchDetails = async () => {
        setIsRandomObsLoading(true);
        setRandomObsError(null);
        try {
          // Fetch submitted observation details
          const api = createApiClient();
          let details: any = null;
          let questions = null;
          // Fetch details using correct endpoints
          if (
            obs.observation_type === "Pulse Check" ||
            obs.observation_type === "Routine" ||
            obs.observation_type === "Full"
          ) {
            details = await apiClient(
              api,
              `/observations/${obs.observation_id}`,
              "GET"
            );
            // Extract type and role from details
            console.log(
              `Fetched details for observation ID ${obs.observation_id}:`,
              details
            ); // Log the fetched details
            const modifiedBy = details.Modified_By;
            const createdBy = details.Created_By;
            if (modifiedBy) {
              dispatch(setmodifiedBy(modifiedBy));
            }
            const type = details?.New_Hire_Annual_or_Routine;
            const role = details?.Role_Observed;
            // Build query string for questions endpoint (role first, then observation_type)
            let questionsUrl = "/observations/questions";
            const params = [];
            if (role) params.push(`role=${encodeURIComponent(role)}`);
            if (type)
              params.push(`observation_type=${encodeURIComponent(type)}`);
            if (params.length > 0) questionsUrl += `?${params.join("&")}`;
            console.log(`Fetching questions from URL: ${questionsUrl}`); // Log the questions URL
            questions = await apiClient(api, questionsUrl, "GET");

            if (details) {
              // --- Begin explicit Redux state population for Pulse Check ---
              const {
                resetPulseCheck,
                setSelectedRecord,
                setPulseState,
                setfetchedQuestions,
                toggleOption,
                toggleReferenceQuestion,
                setFormValue,
                setNotes,
                setMetGoal,
                setPulseImage,
                setManagerSignature,
                setEmployeeSignature,
                setMiscImageDescription,
              } = require("../store/slices/pulseCheckSlice");

              // Reset pulse check state
              dispatch(resetPulseCheck());
              dispatch(setSelectedRecord(null)); // Reset selected record
              // Set basic record info
              const firstName = details?.EE_First_Name || "";
              const lastName = details?.EE_Last_Name || "";
              const fullName = `${firstName} ${lastName}`.trim();

              console.log(`First Name: ${firstName}`);
              console.log(`Last Name: ${lastName}`);
              console.log(`Full Name: ${fullName}`);
              // console.log(`Setting selected record for observation ID: ${obs.observation_id}`);

              dispatch(
                setSelectedRecord({
                  id: obs.observation_id,
                  name: fullName,
                  date: details?.created_at || new Date().toISOString(),
                  type: type,
                  employeeInfo: {
                    firstName: firstName,
                    lastName: lastName,
                    fullName: fullName,
                    role: role || "Unknown Role",
                  },
                })
              );

              console.log("Redux setSelectedRecord payload:", {
                id: obs.observation_id,
                name: fullName,
                employeeInfo: {
                  firstName: firstName,
                  lastName: lastName,
                  fullName: fullName,
                  role: role || "Unknown Role",
                },
              });

              // Set observation filters for PulseCheck component
              const {
                setSelectedFilters: setObservationFilters,
              } = require("../store/slices/observationSlice");

              dispatch(
                setObservationFilters({
                  role: role || "Site Leader",
                  observationType: type || "Pulse Check",
                  employee: fullName,
                  employeeFirstName: firstName,
                  employeeLastName: lastName,
                  shift: details?.Shift_Number || "Day",
                })
              );

              // Set pulse state based on observation status
              const status = details?.status?.toLowerCase() || "completed";
              dispatch(
                setPulseState(
                  status === "completed" ? "completed" : "in-progress"
                )
              );

              // Store questions
              const questionsArray = Array.isArray(questions?.data)
                ? questions.data
                : [];
              dispatch(setfetchedQuestions(questionsArray));

              // Populate selected options/questions from toolsMapping
              if (
                details?.toolsMapping &&
                Array.isArray(details.toolsMapping)
              ) {
                details.toolsMapping.forEach((tool: any) => {
                  const question = questionsArray.find(
                    (q: any) => q.id?.toString() === tool.toolId?.toString()
                  );
                  if (question) {
                    // Determine if it's a regular option or reference question based on question type/category
                    if (
                      question.category === "reference" ||
                      question.type === "reference"
                    ) {
                      dispatch(
                        toggleReferenceQuestion({
                          id: question.id?.toString() || "",
                          label: question.question || question.label || "",
                          Pallet_Type: question.Pallet_Type || "",
                        })
                      );
                    } else {
                      dispatch(
                        toggleOption({
                          id: question.id?.toString() || "",
                          label: question.question || question.label || "",
                          Pallet_Type: question.Pallet_Type || "",
                        })
                      );
                    }
                  }
                });
              }

              // Populate form values from walkData
              if (details?.walkData) {
                const walkData = details.walkData;
                if (walkData.metric1)
                  dispatch(
                    setFormValue({ field: "metric1", value: walkData.metric1 })
                  );
                if (walkData.result1)
                  dispatch(
                    setFormValue({ field: "result1", value: walkData.result1 })
                  );
                if (walkData.target1)
                  dispatch(
                    setFormValue({ field: "target1", value: walkData.target1 })
                  );
                if (walkData.metric2)
                  dispatch(
                    setFormValue({ field: "metric2", value: walkData.metric2 })
                  );
                if (walkData.result2)
                  dispatch(
                    setFormValue({ field: "result2", value: walkData.result2 })
                  );
                if (walkData.goal)
                  dispatch(
                    setFormValue({ field: "goal", value: walkData.goal })
                  );

                // Set notes
                if (walkData.notes)
                  dispatch(setNotes({ which: "first", value: walkData.notes }));
                if (walkData.secondaryNotes)
                  dispatch(
                    setNotes({
                      which: "second",
                      value: walkData.secondaryNotes,
                    })
                  );

                // Set met goal
                if (typeof walkData.metGoal === "boolean")
                  dispatch(setMetGoal(walkData.metGoal));

                // Set misc image descriptions
                if (walkData.miscellImageDesc || walkData.miscellImage2Desc) {
                  dispatch(
                    setMiscImageDescription({
                      description1: walkData.miscellImageDesc || "",
                      description2: walkData.miscellImage2Desc || "",
                    })
                  );
                }

                // Set manager signature - always dispatch to ensure component renders
                dispatch(
                  setManagerSignature({
                    fileName: walkData.managerSignature || "",
                    imageUrl: walkData.managerSignature || "",
                    isNewUpload: false,
                  })
                );

                // Set employee signature - always dispatch to ensure component renders
                dispatch(
                  setEmployeeSignature({
                    fileName: walkData.employeeSignature || "",
                    imageUrl: walkData.employeeSignature || "",
                    isNewUpload: false,
                  })
                );

                // Set pulse images from walkData
                if (walkData.miscellImage) {
                  dispatch(
                    setPulseImage({
                      imageId: "misc1",
                      imageBase64: walkData.miscellImage,
                      imagePreview: walkData.miscellImage,
                      description: walkData.miscellImageDesc || "",
                      observationId: obs.observation_id?.toString() || "",
                    })
                  );
                }

                if (walkData.miscellImage2) {
                  dispatch(
                    setPulseImage({
                      imageId: "misc2",
                      imageBase64: walkData.miscellImage2,
                      imagePreview: walkData.miscellImage2,
                      description: walkData.miscellImage2Desc || "",
                      observationId: obs.observation_id?.toString() || "",
                    })
                  );
                }
              }

              // --- End Redux prepopulation for Pulse Check ---
            }

            setObservationDetails({ details, questions: questions?.data });
          } else if (obs.observation_type === "Shift Handover") {
            // Always fetch fresh details for Shift Handover to avoid stale Redux state
            details = await apiClient(
              api,
              `/shift-handover-walks/${obs.observation_id}`,
              "GET"
            );
            questions = await apiClient(
              api,
              `/shift-handover-walks/questions`,
              "GET"
            );
            console.log("Shift Handover Details:", details);
            console.log("Shift Handover Questions raw:", questions);
            const modifiedBy = details?.walkData?.modifiedBy;
            if (modifiedBy) {
              dispatch(setmodifiedBy(modifiedBy));
            }
            if (details) {
              // --- Begin explicit Redux state population ---
              const {
                setSelectedShiftHandOver,
                setLoading,
                setError,
                setHandoverState,
                setSelectedAttendees,
                toggleShiftHandoverOption,
                setObservationNotes,
                setMiscObservationImage,
                setSignatureFileName,
                setSelectedShift,
                resetShiftHandoverImages,
                setShiftHandoverImage,
                setShiftHandoverQuestions,
              } = require("../store/slices/shiftHandOverSlice");

              // Reset state
              dispatch(resetShiftHandoverState());
              dispatch(setLoading(true));
              dispatch(setError(null));
              dispatch(setSelectedShiftHandOver(details));
              dispatch(setHandoverState(details.status || "new"));
              dispatch(
                setObservationNotes(
                  details?.walkData?.notes || details?.Notes || ""
                )
              );
              dispatch(
                setSignatureFileName({
                  fileName: details?.walkData?.managerSignature || "",
                  imageUrl: details?.walkData?.managerSignature || "",
                })
              );
              dispatch(
                setSelectedShift({
                  id:
                    details?.walkData?.shift?.id?.toString() ||
                    details?.walkData?.shift?.toString() ||
                    details?.Shift?.toString() ||
                    "",
                  displayText:
                    details?.walkData?.shift?.displayText ||
                    details?.walkData?.shift?.toString() ||
                    details?.Shift?.toString() ||
                    "",
                })
              );
              // Attendees
              let attendeesArr = [];
              if (details?.walkData?.attendees) {
                try {
                  attendeesArr = JSON.parse(details.walkData.attendees);
                } catch {
                  attendeesArr = details.walkData.attendees
                    .split(",")
                    .map((name: any, idx: any) => ({
                      id: String(idx),
                      displayName: name.trim(),
                      userPrincipalName: "",
                    }));
                }
              }
              dispatch(setSelectedAttendees(attendeesArr));
              // Questions
              const questionsArray = Array.isArray(questions?.data)
                ? questions.data
                : Array.isArray(questions)
                ? questions
                : [];
              dispatch(setShiftHandoverQuestions(questionsArray));
              console.log("Shift Handover Questions:", questionsArray); // Debugging log
              // Store toolsMapping array in selectedQuestions in Redux, only after questions are set
              if (
                details?.toolsMapping &&
                Array.isArray(details.toolsMapping)
              ) {
                // Map after questions are set
                const mappedSelectedQuestions = details.toolsMapping.map(
                  (tool: any) => {
                    const match = Array.isArray(questionsArray)
                      ? questionsArray.find(
                          (q: any) =>
                            q.id?.toString() === tool.toolId?.toString()
                        )
                      : null;
                    if (match) {
                      return {
                        id: match.id?.toString(),
                        questionText: match.questionText,
                        category: match.category,
                      };
                    } else {
                      // fallback: store id and toolName if no match
                      return {
                        id: tool.toolId?.toString(),
                        questionText: tool.toolName || "",
                        category: "",
                      };
                    }
                  }
                );
                dispatch(
                  require("../store/slices/shiftHandOverSlice").setSelectedQuestions(
                    mappedSelectedQuestions
                  )
                );
              }
              // Misc images
              if (
                details?.walkData?.miscellImage ||
                details?.walkData?.miscellImageDesc
              ) {
                dispatch(
                  setMiscObservationImage({
                    index: 0,
                    image: {
                      imageUrl: details.walkData.miscellImage || "",
                      fileName: "",
                      description: details.walkData.miscellImageDesc || "",
                      index: 0,
                    },
                  })
                );
              }
              if (
                details?.walkData?.miscellImage2 ||
                details?.walkData?.miscellImage2Desc
              ) {
                dispatch(
                  setMiscObservationImage({
                    index: 1,
                    image: {
                      imageUrl: details.walkData.miscellImage2 || "",
                      fileName: "",
                      description: details.walkData.miscellImage2Desc || "",
                      index: 1,
                    },
                  })
                );
              }
              // Images
              dispatch(resetShiftHandoverImages());
              if (
                details?.shiftHandoverImages &&
                Array.isArray(details.shiftHandoverImages)
              ) {
                details.shiftHandoverImages.forEach((img: any) => {
                  dispatch(
                    setShiftHandoverImage({
                      imageId: img.ImageId?.toString() || "",
                      imageBase64: img.ImageBase64 || "",
                      imagePreview: img.ImageBase64 || "",
                      description: img.Description || "",
                      questionId: img.ObservationId?.toString() || "",
                    })
                  );
                });
              }
              dispatch(setLoading(false));
              setObservationDetails({ details, questions: questions?.data });
            } else {
              setRandomObsError(
                "No details found for Shift Handover observation."
              );
              setObservationDetails(null);
            }
          } else if (obs.observation_type === "SPARC Walk") {
            // For SPARC Walk, set up the state to show the loader component
            details = await apiClient(
              api,
              `/sparc-walks/${obs.observation_id}`,
              "GET"
            );
            console.log("SPARC Walk details response:", details); // <--- Add this line
            const modifiedBy = details?.walkData?.modifiedBy;
            if (modifiedBy) {
              dispatch(setmodifiedBy(modifiedBy));
            }
            setCurrentSparcWalkObs(obs);
            setShowSparcWalkLoader(true);
            setSparcWalkReady(false);
            setObservationDetails({ details: null, questions: null });
          } else if (obs.observation_type === "Leadership Walk") {
            details = await apiClient(
              api,
              `/leadership-walks/${obs.observation_id}`,
              "GET"
            );
            // Extract frequency, walkType, and zone from details
            const frequency =
              details?.walkData?.frequency || details?.walkData?.Frequency;
            const walkType = details?.walkData?.walkType || "Leadership Walk";
            const zone = details?.walkData?.zone || details?.data?.Zone;
            let questionsUrl = "/leadership-walks/questions";
            const params = [];
            if (frequency)
              params.push(`frequency=${encodeURIComponent(frequency)}`);
            if (walkType)
              params.push(`walkType=${encodeURIComponent(walkType)}`);
            if (zone) params.push(`zoneIds=${encodeURIComponent(zone)}`);
            if (params.length > 0) questionsUrl += `?${params.join("&")}`;
            questions = await apiClient(api, questionsUrl, "GET");
            const modifiedBy = details?.walkData?.modifiedBy;
            if (modifiedBy) {
              dispatch(setmodifiedBy(modifiedBy));
            }
            // Defensive: fallback to details.questions if API returns no questions
            let questionsData = questions?.data;
            if (
              !questionsData ||
              (typeof questionsData === "object" &&
                Object.keys(questionsData).length === 0)
            ) {
              // Try to use questions from details if present
              if (details?.questions) {
                questionsData = details.questions;
              } else if (details?.walkData?.questions) {
                questionsData = details.walkData.questions;
              }
            }

            // --- Prepopulate Redux state for Leadership Walk Redirect ---
            const {
              setSelectedLeadershipWalk,
              setLoading: setLeadershipWalkLoading,
              setSelectedZone,
              setQuestions: setLeadershipWalkQuestions,
              setWalkId,
              setSelectedFrequency, // <-- Add this import
            } = require("../store/slices/leaderShipWalkSlice");
            const {
              setQuestions: setRedirectQuestions,
              setSelectedQuestions,
              setSelectedRecord,
              setWalkState,
              setMiscObservationImage,
              setManagerSignature,
              resetSelectedOptions,
              toggleOption,
              setLoading,
            } = require("../store/slices/leadershipWalkRedirectSlice");

            dispatch(setLeadershipWalkLoading(true));
            dispatch(setLoading(true)); // leadershipWalkRedirectSlice loading true

            dispatch(setSelectedLeadershipWalk(details));

            if (details?.walkData?.zone || details?.Zone) {
              const zoneVal = details?.walkData?.zone || details?.Zone;
              dispatch(setSelectedZone([{ ZONE: zoneVal, ZONE_ID: zoneVal }]));
            }

            // --- Set frequency in Redux for Leadership Walk ---
            if (frequency) {
              dispatch(setSelectedFrequency(frequency));
            }

            // --- Normalize/group questions for Leadership Walk ---
            let lwQuestionsData = questions?.data;
            console.log(
              "Leadership Walk raw questions:",
              JSON.stringify(questions, null, 2)
            );
            console.log(
              "Leadership Walk details:",
              JSON.stringify(details, null, 2)
            );
            if (
              !lwQuestionsData ||
              (typeof lwQuestionsData === "object" &&
                Object.keys(lwQuestionsData).length === 0)
            ) {
              if (details?.questions) {
                lwQuestionsData = details.questions;
              } else if (details?.walkData?.questions) {
                lwQuestionsData = details.walkData.questions;
              }
            }

            dispatch(setQuestions(questions));

            dispatch(setSelectedQuestions(details));
            dispatch(
              setWalkId(
                Number(
                  details?.walkData?.id ||
                    details?.id ||
                    details?.ID ||
                    obs.observation_id
                )
              )
            );
            dispatch(
              setSelectedRecord({
                id: String(
                  details?.walkData?.id ||
                    details?.id ||
                    details?.ID ||
                    obs.observation_id
                ),
                name:
                  details?.walkData?.location ||
                  details?.walkData?.zone ||
                  details?.Zone ||
                  "",
              })
            );

            if (details?.walkData?.status) {
              const status = details.walkData.status.toLowerCase();
              if (status === "completed") dispatch(setWalkState("completed"));
              else if (status === "in progress" || status === "in_progress")
                dispatch(setWalkState("in_progress"));
              else dispatch(setWalkState("not_started"));
            } else {
              dispatch(setWalkState("not_started"));
            }

            if (details?.walkData?.miscellImage) {
              dispatch(
                setMiscObservationImage({
                  index: 0,
                  image: {
                    imageUrl: details.walkData.miscellImage,
                    fileName: "",
                    description: details.walkData.miscellImageDesc || "",
                    index: 0,
                  },
                })
              );
            }
            if (details?.walkData?.miscellImage2) {
              dispatch(
                setMiscObservationImage({
                  index: 1,
                  image: {
                    imageUrl: details.walkData.miscellImage2,
                    fileName: "",
                    description: details.walkData.miscellImage2Desc || "",
                    index: 1,
                  },
                })
              );
            }
            if (details?.walkData?.managerSignature) {
              dispatch(
                setManagerSignature({
                  fileName: details.walkData.managerSignature,
                  imageUrl: details.walkData.managerSignature,
                  isNewUpload: false,
                })
              );
            }
            dispatch(resetSelectedOptions());
            if (details?.toolsMapping && Array.isArray(details.toolsMapping)) {
              (details.toolsMapping as any[]).forEach((tool: any) => {
                dispatch(
                  toggleOption({
                    id: tool.toolId?.toString() || "",
                    label: tool.toolName || "",
                  })
                );
              });
            }
            dispatch(setLoading(false)); // leadershipWalkRedirectSlice loading false
            // --- End Redux prepopulation---
          } else {
            details = await apiClient(api, `/${obs.observation_id}`, "GET");
          }
          setObservationDetails({
            details: details?.data,
            questions: questions?.data,
          });
        } catch (err: any) {
          setRandomObsError(
            err.message || "Failed to fetch observation details"
          );
          setObservationDetails(null);
        } finally {
          setIsRandomObsLoading(false);
        }
      };
      fetchDetails();
    } else {
      setObservationDetails(null);
    }
  }, [randomObservations, currentObservationIndex]);

  // Fetch table and audit insights data on initial load and when location changes
  // Also reset region selection when location changes
  useEffect(() => {
    console.log("Location changed, resetting region selection and fetching data");
    
    // Reset region when location changes
    setAuditFilters((prevFilters) => ({
      ...prevFilters,
      region: "",
      regionOptions: [],
    }));
    
    // Fetch data for new location
    fetchTableData();
    fetchAuditInsightsData();
  }, [selectedLocation]);

  // Clear form data when observation index changes to prevent carrying over data from previous observations
  useEffect(() => {
    console.log("QualityAudit: Observation index changed, clearing form data");
    // Use setTimeout to ensure this runs after any React state updates
    const timeoutId = setTimeout(() => {
      setReviewType(null);
      setReviewStatus([]);
      setReviewComment("");
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [currentObservationIndex]);


  // If redirect is active, show the QualityAuditRedirect screen
  if (
    randomObservations.length > 0 &&
    currentObservationIndex < randomObservations.length
  ) {
    return (
      <>
      <QualityAuditRedirect
        randomObservations={randomObservations}
        currentObservationIndex={currentObservationIndex}
        observationDetails={observationDetails}
        isRandomObsLoading={isRandomObsLoading}
        randomObsError={randomObsError}
        addButtonEnabled={addButtonEnabled}
        handleOpenReviewOverlay={handleOpenReviewOverlay}
        handleCloseReviewOverlay={handleCloseReviewOverlay}
        showReviewOverlay={showReviewOverlay}
        reviewType={reviewType}
        setReviewType={setReviewType}
        reviewStatus={reviewStatus}
        setReviewStatus={setReviewStatus}
        reviewComment={reviewComment}
        setReviewComment={setReviewComment}
        handleSubmitReview={handleSubmitReview}
        showAddOverlay={showAddOverlay}
        handleOpenAddOverlay={handleOpenAddOverlay}
        handleCloseAddOverlay={handleCloseAddOverlay}
        submissionTypeOptions={submissionTypeOptions}
        submissionDateOptions={submissionDateOptions}
        handleAddRandomObservation={handleAddRandomObservation}
        auditFilters={auditFilters}
        userEmail={userEmail}
        setAuditFilters={setAuditFilters}
        setSubmissionTypeOptions={setSubmissionTypeOptions}
        setSubmissionDateOptions={setSubmissionDateOptions}
        isSubmittingReview={isSubmittingReview}
        sparcWalkReady={sparcWalkReady}
        showSparcWalkLoader={showSparcWalkLoader}
        currentSparcWalkObs={currentSparcWalkObs}
        setRandomObservations={setRandomObservations}
        setCurrentObservationIndex={setCurrentObservationIndex}
        setObservationDetails={setObservationDetails}
        setShowSparcWalkLoader={setShowSparcWalkLoader}
        setSparcWalkReady={setSparcWalkReady}
        setCurrentSparcWalkObs={setCurrentSparcWalkObs}
        onRedirectComplete={handleRedirectComplete}
        isFromBeginButton={isFromBeginButton}
        addOverlayError={addOverlayError}
        handleRedirectBackPress={handleRedirectBackPress}
      />
      {/* Ensure error modal is mounted even when redirect screen is active */}
      <QualityAuditErrorModal />
      </>
    );
  }
  if (showQualityAuditRedirect) {
    return (
      <>
      <QualityAuditRedirect
        randomObservations={randomObservations}
        currentObservationIndex={currentObservationIndex}
        observationDetails={observationDetails}
        isRandomObsLoading={isRandomObsLoading}
        randomObsError={randomObsError}
        addButtonEnabled={addButtonEnabled}
        handleOpenReviewOverlay={handleOpenReviewOverlay}
        handleCloseReviewOverlay={handleCloseReviewOverlay}
        showReviewOverlay={showReviewOverlay}
        reviewType={reviewType}
        setReviewType={setReviewType}
        reviewStatus={reviewStatus}
        setReviewStatus={setReviewStatus}
        reviewComment={reviewComment}
        setReviewComment={setReviewComment}
        handleSubmitReview={handleSubmitReview}
        showAddOverlay={showAddOverlay}
        handleOpenAddOverlay={handleOpenAddOverlay}
        handleCloseAddOverlay={handleCloseAddOverlay}
        submissionTypeOptions={submissionTypeOptions}
        submissionDateOptions={submissionDateOptions}
        handleAddRandomObservation={handleAddRandomObservation}
        auditFilters={auditFilters}
        userEmail={userEmail}
        setAuditFilters={setAuditFilters}
        setSubmissionTypeOptions={setSubmissionTypeOptions}
        setSubmissionDateOptions={setSubmissionDateOptions}
        isSubmittingReview={isSubmittingReview}
        sparcWalkReady={sparcWalkReady}
        showSparcWalkLoader={showSparcWalkLoader}
        currentSparcWalkObs={currentSparcWalkObs}
        setRandomObservations={setRandomObservations}
        setCurrentObservationIndex={setCurrentObservationIndex}
        setObservationDetails={setObservationDetails}
        setShowSparcWalkLoader={setShowSparcWalkLoader}
        setSparcWalkReady={setSparcWalkReady}
        setCurrentSparcWalkObs={setCurrentSparcWalkObs}
        onRedirectComplete={handleRedirectComplete}
        isFromBeginButton={isFromBeginButton}
        addOverlayError={addOverlayError}
        handleRedirectBackPress={function (): void {
          throw new Error("Function not implemented.");
        }}
      />
      {/* Ensure error modal is mounted even when redirect screen is active */}
      <QualityAuditErrorModal />
      </>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={{ flex: 1, backgroundColor: isDarkMode ? colors.background.page : "#F5F9FF" }}
    >
      <ScrollView
        style={{ flex: 1, backgroundColor: isDarkMode ? colors.background.page : "#F5F9FF" }}
        contentContainerStyle={[
          styles.container,
          { flexGrow: 1, flex: undefined },
          isDarkMode && { backgroundColor: colors.background.page },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={true}
        scrollEnabled={true}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.brand.primary}
            colors={[colors.brand.primary]}
            title="Refreshing..."
            titleColor={isDarkMode ? colors.text.primary : colors.brand.primary}
          />
        }
      >
        <QualityAuditWelcomeCard
          onBeginButtonClick={handleBeginButtonClick}
          showAuditActiveOnly={showAuditActiveOnly}
          onToggleAuditInsights={() => setShowAuditActiveOnly((prev) => !prev)}
        />

        {/* Quality Audit Begin Overlay */}
        <QualityAuditBegin
          isFilterOverlayVisible={isBeginFilterOverlayVisible}
          closeFilterOverlay={closeBeginFilterOverlay}
          handleSaveFilters={() => handleSaveAuditFilters()}
          selectedRole={auditFilters.role}
          auditFilters={auditFilters}
          setAuditFilters={setAuditFilters}
          showValidation={showValidation}
        />

        <AuditInsightsTab
          completedSiteLeaderCount={completedSiteLeaderCount}
          completedRegionalLeaderCount={completedRegionalLeaderCount}
          selectedRowData={selectedRowData}
          isOverlayVisible={isOverlayVisible}
          closeOverlay={closeOverlay}
        />
        <QualityAuditMetrics
          filterOptions={filterOptions1}
          onFilterChange={handleFilterChange}
          isMobile={isMobile}
          isLoading={isLoading}
          tableData={filteredAuditInsightsData}
          tableColumns={tableColumns1}
          onRowClick={handleRowClick}
          styles={styles}
          resetFilters={resetFilters}
          showAuditActiveOnly={showAuditActiveOnly}
          onToggleAuditInsights={() => setShowAuditActiveOnly((prev) => !prev)}
        />

        {/* Display the most recently fetched random observation */}
        {randomObsError ? (
          <View
            style={{
              marginVertical: 16,
              padding: 16,
              backgroundColor: isDarkMode ? colors.statusBackground.warning : "#fff3cd",
              borderRadius: 8,
            }}
          >
            <Text style={{ color: isDarkMode ? colors.text.primary : "#856404", fontWeight: "bold", fontFamily: "regular" }}>
              Error: {randomObsError}
            </Text>
          </View>
        ) : randomObservations.length > 0 ? (
          <View
            style={{
              marginVertical: 16,
              padding: 16,
              backgroundColor: isDarkMode ? colors.background.surface : "#f8f9fa",
              borderRadius: 8,
            }}
          >
            <Text style={{ fontWeight: "bold", fontSize: 16, marginBottom: 8, fontFamily: "regular", color: isDarkMode ? colors.text.primary : "#000000" }}>
              Latest Random Observation
            </Text>
            <Text style={{ fontFamily: "regular", color: isDarkMode ? colors.text.secondary : "#000000" }}>
              Observation ID: {randomObservations[0].observation_id || "N/A"}
            </Text>
            <Text style={{ fontFamily: "regular", color: isDarkMode ? colors.text.secondary : "#000000" }}>
              Observation Type:{" "}
              {randomObservations[0].observation_type || "N/A"}
            </Text>
            <Text style={{ fontFamily: "regular", color: isDarkMode ? colors.text.secondary : "#000000" }}>Site: {randomObservations[0].site || "N/A"}</Text>
            {/* Optionally display more details if available */}
            {randomObservations[0].details && (
              <View style={{ marginTop: 8 }}>
                <Text style={{ fontWeight: "bold", fontFamily: "regular", color: isDarkMode ? colors.text.primary : "#000000" }}>Details:</Text>
                <Text style={{ fontFamily: "regular", color: isDarkMode ? colors.text.secondary : "#000000" }}>
                  {JSON.stringify(randomObservations[0].details, null, 2)}
                </Text>
              </View>
            )}
          </View>
        ) : null}

        
      </ScrollView>
      {/* Performance Review Confirmation Overlay */}
      {showPerformanceOverlay && (
        <LHOverlay
          visible={showPerformanceOverlay}
          onDismiss={() => {
            setShowPerformanceOverlay(false);
            setPerformanceReviewComment("");
          }}
          title="Performance Review"
          size="small"
        >
          <View style={{ padding: 16 }}>
            {/* <Text style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 12 }}>Mark Performance Review as Completed</Text> */}
            {/* <Text style={{ marginBottom: 16, color: '#495057' }}>
Are you sure you want to mark this Performance Review as completed?</Text> */}
            <Text style={{ fontWeight: "400", marginBottom: 8, fontFamily: FontFamily.regular, fontSize: 12, color: isDarkMode ? colors.text.secondary : "#333333" }}>
              Feedback
            </Text>
            <TextInput
              style={{
                minHeight: 100,
                borderColor: isDarkMode ? colors.input.border : "#ced4da",
                borderWidth: 1,
                borderRadius: 8,
                padding: 5,
                fontSize: 14,
                backgroundColor: isDarkMode ? colors.input.background : "#f8f9fa",
                marginBottom: 16,
                fontFamily: FontFamily.regular,
                color: isDarkMode ? colors.text.primary : "#000000",
              }}
              placeholder="Enter Performance Review Feedback"
              placeholderTextColor={isDarkMode ? colors.text.disabled : "#6C757D"}
              value={performanceReviewComment}
              onChangeText={setPerformanceReviewComment}
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
            <View
              style={{
                flexDirection: "row",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <LhButton
                text="Cancel"
                variant="secondary"
                size="small"
                onPress={() => {
                  setShowPerformanceOverlay(false);
                  setPerformanceReviewComment("");
                  // Do NOT update auditInsightsData here; leave as is so toggle does not turn green
                }}
                style={{ width: '25%', minWidth: 90 }}
              />
              <LhButton
                text="Save"
                variant="primary"
                size="small"
                onPress={async () => {
                  // Update to Completed
                  const updatedData = auditInsightsData.map((item) =>
                    item.id === performanceRowId
                      ? {
                          ...item,
                          actualStatus: "Completed",
                          performanceReviewComment,
                        }
                      : item
                  );
                  setAuditInsightsData(updatedData);

                  // Get current date in YYYY-MM-DD format
                  const now = new Date();
                  const yyyy = now.getFullYear();
                  const mm = String(now.getMonth() + 1).padStart(2, "0");
                  const dd = String(now.getDate()).padStart(2, "0");
                  const today = `${yyyy}-${mm}-${dd}`;

                  // Prepare API payload
                  const payload = {
                    audit_observations_id: performanceRowId,
                    status: "Completed",
                    performance_review: performanceReviewComment,
                    performance_review_date: today,
                  };
                  console.log("Performance Review API payload:", payload);

                  try {
                    const api = createApiClient();
                    await apiClient(
                      api,
                      "/quality-audit/insight",
                      "POST",
                      payload
                    );
                    // After successful POST, call GET /quality-audit with same parameters if present
                    // Use the same logic as fetchAuditInsightsData (line 155)
                    // If you have parameters, append them as query string
                    // For now, just call GET without params (unless you want to add period/week)
                    //await apiClient(api, "/quality-audit", "GET");
                    await fetchAuditInsightsData(); // <-- Add this line
                  } catch (err) {
                    console.error(
                      "Error posting performance review insight or fetching audit data:",
                      err
                    );
                  }

                  setShowPerformanceOverlay(false);
                  setPerformanceReviewComment("");
                }}
                style={{ width: '25%', minWidth: 90 }}
              />
            </View>
          </View>
        </LHOverlay>
      )}
      {/* Quality Audit centralized error modal */}
      <QualityAuditErrorModal />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Platform.OS === "web" ? 25 : 16,
    backgroundColor: "#F5F9FF",
    paddingVertical: Platform.OS === "ios" ? 12 : "1.5%",
    paddingBottom: Platform.OS === "ios" ? 40 : 16,
  },
  formSection: {},
  sectionHeader: {
    fontSize: Platform.select({ web: 16, default: 14 }),
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 5,
    paddingHorizontal: 10,
    color: "#333",
  },
  sectionHeader1: {
    fontSize: Platform.select({ web: 16, default: 14 }),
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 5,
    paddingHorizontal: 10,
    color: "#333",
    marginLeft: 30,
  },
  sectiontableHeader: {
    backgroundColor: "#004B93",
    padding: 16,
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 5,
  },
  textHeader: {
    color: "#fff",
    fontSize: Platform.select({ web: 16, default: 14 }),
    fontWeight: "bold",
    textAlign: "center",
  },

  dropdownContainer: {
    marginBottom: 10,
  },

  addButton: {
    padding: 12,
    borderRadius: 20,
    alignItems: "center",
    marginLeft: 50,

    width: Platform.OS === "web" ? "15%" : "50%",
  },
  mobileAddButton: {
    alignSelf: "center",
    marginLeft: 0,
    marginRight: 0,
  },
  addButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: Platform.select({ web: 16, default: 14 }),
  },
  divider: {
    height: 1,
    backgroundColor: "#ddd",
    marginVertical: 20,
  },
  filterContainer: {
    marginTop: Platform.select({ web: 10, default: 0 }),
  },
  messageContainer: {
    padding: 16,
    alignItems: "center",
  },
  loadingText: {
    fontSize: 16,
    color: "#0066CC",
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
  },
  returnButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0066CC",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  returnButtonIcon: {
    color: "white",
    fontSize: 16,
    marginRight: 4,
  },
  returnButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "500",
  },
  auditButton: {
    width: Platform.OS === "web" ? "10%" : 140,
    minWidth: 120,
  },
  iconCircle: {
    borderRadius: 20,
    backgroundColor: "#0066CC",
    justifyContent: "center",
    alignItems: "center",
  },
  completedButton: {
    minWidth: Platform.OS === "web" ? 120 : undefined, // ensure enough width on web
    maxWidth: Platform.OS === "web" ? 160 : undefined,
    flexGrow: 1,
    alignSelf: "center",
    paddingHorizontal: 12,
    backgroundColor: "green",
  },
  completedButtonText: {
    fontSize: 14,
    flexShrink: 1,
    textAlign: "center",
  },
  table: {
    marginTop: 20,
    borderWidth: 1,
    borderColor: "#000",
  },
  headerRow: {
    flexDirection: "row",
  },
  fullHeaderCell: {
    flex: 1,
    textAlign: "center",
    padding: 10,
    backgroundColor: "#77ab59",
    color: "#fff",
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
  },
  cellTitle: {
    width: "30%",
    padding: 8,
    borderRightWidth: 1,
    borderColor: "#000",
    fontWeight: "bold",
  },
  cellDesc: {
    width: "70%",
    padding: 8,
  },
  overlayText: {
    fontSize: 16,
    lineHeight: 24,
    color: "#333",
    marginBottom: 20,
  },
  buttonContainer: {
    flexDirection: "column",
    gap: 12,
    marginTop: 20,
  },
  overlayButton: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    borderRadius: 6,
    justifyContent: "center",
    minWidth: 200,
  },
  overlayButtonPrimary: {
    backgroundColor: "#006CC",
  },
  overlayButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "500",
    marginLeft: 8,
  },
  buttonIcon: {
    marginRight: 8,
  },
  actionItemButton: {
    color: "#fff",
    width: Platform.OS === "web" ? "10%" : 140,
    minWidth: 120,
    backgroundColor: "#4CAF50",
    borderColor: "#4CAF50",
  },
  buttonRow: {
    marginTop: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    marginBottom: 16,
    marginLeft: 10,
  },
  buttonRowContainer: {
    // Add button row container
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 10,
    gap: 10,
  },
  buttonStyle: {
    width: "auto",
    minWidth: Platform.OS === "web" ? 120 : 60,
    fontSize: Dimensions.get("window").width < 400 ? 12 : 16,
    marginRight: "1%",
  },
  buttonTextStyle: {
    fontSize: 14,
    flexWrap: "nowrap",
    textAlign: "center",
  },
  // Styles for top dropdowns row
  topDropdownsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    marginTop: 8,
    paddingHorizontal: 4,
    zIndex: 100, // Ensure dropdowns appear above other elements
  },
  topDropdownItem: {
    width: "48%", // Almost half width with some spacing between
    zIndex: 100,
  },
  dropdownLabel: {
    fontSize: 14,
    fontWeight: "500",
    marginBottom: 4,
    color: "#333",
  },
  topDropdown: {
    width: "100%",
    zIndex: 100,
    position: "relative",
    minHeight: 40,
  },
  tableCellText: {
    color: "#5e666cff", // or whatever color you want for normal cells
    fontSize: 14,
  },
  dataTableContainer: {
    ...Platform.select({
      ios: {
        shadowColor: "#0066CC",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
      },
      android: {
        elevation: 8,
        shadowColor: "#0066CC",
      },
      web: {
        boxShadow: "0 4px 12px 0 rgba(0, 102, 204, 0.4)",
      },
    }),
    backgroundColor: "#fff",
    borderRadius: 8,
    marginTop: 16,
  },
});

export default QualityAudit;
